'use client';

import { useState, useRef, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import { useTheme } from '@/contexts/ThemeContext';

interface NavItem {
  label: string;
  path: string;
  icon: string;
  tooltip: string;
  shortcut: string;
}

interface ConnectionSource {
  name: string;
  status: 'connected' | 'degraded' | 'disconnected';
  latency?: number;
}

const NAV_ITEMS: NavItem[] = [
  {
    label: 'Market Overview',
    path: '/market-overview-dashboard',
    icon: 'ChartBarIcon',
    tooltip: 'Real-time market surveillance and monitoring',
    shortcut: 'Alt+1',
  },
  {
    label: 'Asset Analysis',
    path: '/asset-analysis-screen',
    icon: 'MagnifyingGlassChartIcon',
    tooltip: 'Comprehensive individual asset evaluation',
    shortcut: 'Alt+2',
  },
  {
    label: 'Portfolio',
    path: '/portfolio-management',
    icon: 'BriefcaseIcon',
    tooltip: 'Manage investment portfolios and positions',
    shortcut: 'Alt+3',
  },
  {
    label: 'Strategy Backtest',
    path: '/strategy-backtest',
    icon: 'BeakerIcon',
    tooltip: 'Build and backtest trading strategies',
    shortcut: 'Alt+4',
  },
  {
    label: 'Analytics',
    path: '/performance-analytics',
    icon: 'PresentationChartLineIcon',
    tooltip: 'Performance analytics, strategy history and P&L',
    shortcut: 'Alt+6',
  },
  {
    label: 'Paper Trading',
    path: '/paper-trading',
    icon: 'CurrencyDollarIcon',
    tooltip: 'Simulated trading with live market data',
    shortcut: 'Alt+7',
  },
  {
    label: 'Watchlist',
    path: '/split-watchlist',
    icon: 'ViewColumnsIcon',
    tooltip: 'Split-view multi-asset watchlist with live charts',
    shortcut: 'Alt+8',
  },
  {
    label: 'Backtesting',
    path: '/backtesting',
    icon: 'ClockIcon',
    tooltip: 'Advanced backtesting with rule builder and replay',
    shortcut: 'Alt+9',
  },
];

const OVERFLOW_ITEMS: NavItem[] = [
  {
    label: 'Settings',
    path: '/user-settings',
    icon: 'Cog6ToothIcon',
    tooltip: 'Platform configuration and preferences',
    shortcut: 'Alt+5',
  },
];

const INITIAL_CONNECTIONS: ConnectionSource[] = [
  { name: 'Polygon.io', status: 'connected', latency: 12 },
  { name: 'Twelve Data', status: 'connected', latency: 24 },
];

const statusColor: Record<ConnectionSource['status'], string> = {
  connected: 'bg-success',
  degraded: 'bg-warning',
  disconnected: 'bg-error',
};

const statusLabel: Record<ConnectionSource['status'], string> = {
  connected: 'Live',
  degraded: 'Degraded',
  disconnected: 'Offline',
};

interface HeaderProps {
  isOpen?: boolean;
  onMenuToggle?: (open: boolean) => void;
}

const Header = ({ isOpen = false, onMenuToggle }: HeaderProps) => {
  const pathname = usePathname();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(isOpen);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [connectionMenuOpen, setConnectionMenuOpen] = useState(false);
  const [overflowMenuOpen, setOverflowMenuOpen] = useState(false);
  const [connections] = useState<ConnectionSource[]>(INITIAL_CONNECTIONS);
  const [currentTime, setCurrentTime] = useState('');
  const { resolvedTheme, toggleTheme } = useTheme();

  const userMenuRef = useRef<HTMLDivElement>(null);
  const connectionMenuRef = useRef<HTMLDivElement>(null);
  const overflowMenuRef = useRef<HTMLDivElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const tick = () => {
      const now = new Date();
      setCurrentTime(
        now.toLocaleTimeString('en-US', {
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false,
          timeZone: 'America/New_York',
        }) + ' ET'
      );
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.altKey && e.key === '1') { window.location.href = '/market-overview-dashboard'; }
      if (e.altKey && e.key === '2') { window.location.href = '/asset-analysis-screen'; }
      if (e.altKey && e.key === '3') { window.location.href = '/portfolio-management'; }
      if (e.altKey && e.key === '4') { window.location.href = '/strategy-backtest'; }
      if (e.altKey && e.key === '5') { window.location.href = '/user-settings'; }
      if (e.altKey && e.key === '6') { window.location.href = '/performance-analytics'; }
      if (e.altKey && e.key === '7') { window.location.href = '/paper-trading'; }
      if (e.altKey && e.key === '8') { window.location.href = '/split-watchlist'; }
      if (e.altKey && e.key === '9') { window.location.href = '/backtesting'; }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) setUserMenuOpen(false);
      if (connectionMenuRef.current && !connectionMenuRef.current.contains(e.target as Node)) setConnectionMenuOpen(false);
      if (overflowMenuRef.current && !overflowMenuRef.current.contains(e.target as Node)) setOverflowMenuOpen(false);
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(e.target as Node)) {
        setMobileMenuOpen(false);
        onMenuToggle?.(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onMenuToggle]);

  const toggleMobileMenu = () => {
    const next = !mobileMenuOpen;
    setMobileMenuOpen(next);
    onMenuToggle?.(next);
  };

  const aggregateStatus: ConnectionSource['status'] = connections.some(c => c.status === 'disconnected')
    ? 'disconnected'
    : connections.some(c => c.status === 'degraded')
    ? 'degraded' :'connected';

  const isActive = (path: string) => pathname === path;

  return (
    <header className="fixed top-0 left-0 right-0 h-header bg-card border-b border-border z-nav flex items-center">
      {/* Logo */}
      <div className="flex items-center pl-6 pr-8 h-full border-r border-border shrink-0">
        <Link href="/market-overview-dashboard" className="flex items-center gap-2 group focus:outline-none focus-visible:ring-1 focus-visible:ring-ring rounded">
          <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <rect width="28" height="28" rx="4" fill="#0EA5E9" fillOpacity="0.15" />
            <polyline points="4,20 10,12 16,16 24,6" stroke="#0EA5E9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <circle cx="24" cy="6" r="2" fill="#F59E0B" />
            <line x1="4" y1="22" x2="24" y2="22" stroke="#334155" strokeWidth="1" />
          </svg>
          <span className="font-heading font-700 text-base tracking-tight text-foreground group-hover:text-primary transition-financial hidden sm:block">
            APEX<span className="text-primary">FI</span>
          </span>
        </Link>
      </div>

      {/* Primary Nav */}
      <nav className="hidden md:flex items-center h-full px-2 gap-1" aria-label="Primary navigation">
        {NAV_ITEMS.map((item) => (
          <Link
            key={item.path}
            href={item.path}
            title={`${item.tooltip} (${item.shortcut})`}
            className={`
              relative flex items-center gap-2 px-4 h-full text-sm font-body font-medium transition-financial
              focus:outline-none focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-card rounded
              ${isActive(item.path)
                ? 'text-primary after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-primary after:rounded-t' :'text-muted-foreground hover:text-foreground hover:-translate-y-px'
              }
            `}
            aria-current={isActive(item.path) ? 'page' : undefined}
          >
            <Icon name={item.icon as any} size={16} variant="outline" />
            {item.label}
          </Link>
        ))}

        {/* Overflow menu */}
        <div className="relative h-full flex items-center" ref={overflowMenuRef}>
          <button
            onClick={() => setOverflowMenuOpen(v => !v)}
            className={`
              flex items-center gap-1.5 px-3 h-full text-sm font-body font-medium transition-financial
              focus:outline-none focus-visible:ring-1 focus-visible:ring-ring rounded
              ${OVERFLOW_ITEMS.some(i => isActive(i.path))
                ? 'text-primary' :'text-muted-foreground hover:text-foreground'
              }
            `}
            aria-expanded={overflowMenuOpen}
            aria-haspopup="true"
          >
            <Icon name="EllipsisHorizontalIcon" size={16} variant="outline" />
            More
            <Icon name="ChevronDownIcon" size={12} variant="outline" className={`transition-financial ${overflowMenuOpen ? 'rotate-180' : ''}`} />
          </button>
          {overflowMenuOpen && (
            <div className="absolute top-full left-0 mt-1 w-48 bg-popover border border-border rounded-md shadow-dropdown z-dropdown overflow-hidden">
              {OVERFLOW_ITEMS.map((item) => (
                <Link
                  key={item.path}
                  href={item.path}
                  onClick={() => setOverflowMenuOpen(false)}
                  className={`
                    flex items-center gap-3 px-4 py-3 text-sm transition-financial
                    ${isActive(item.path) ? 'text-primary bg-primary/10' : 'text-card-foreground hover:bg-surface-elevated hover:text-foreground'}
                  `}
                >
                  <Icon name={item.icon as any} size={16} variant="outline" />
                  <span className="flex-1">{item.label}</span>
                  <span className="text-xs text-muted-foreground font-data">{item.shortcut}</span>
                </Link>
              ))}
            </div>
          )}
        </div>
      </nav>

      {/* Spacer */}
      <div className="flex-1" />

      {/* Right utilities */}
      <div className="flex items-center gap-2 pr-6">
        {/* Market time */}
        <div className="hidden lg:flex items-center gap-2 px-3 py-1.5 bg-surface rounded border border-border">
          <Icon name="ClockIcon" size={14} variant="outline" className="text-muted-foreground" />
          <span className="font-data text-xs text-muted-foreground">{currentTime}</span>
        </div>

        {/* Theme toggle */}
        <button
          onClick={toggleTheme}
          title={`Switch to ${resolvedTheme === 'dark' ? 'light' : 'dark'} mode (Ctrl+Shift+T)`}
          className="flex items-center justify-center w-9 h-9 rounded border border-border bg-surface hover:border-primary/40 hover:bg-surface-elevated transition-financial focus:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          aria-label={`Switch to ${resolvedTheme === 'dark' ? 'light' : 'dark'} mode`}
        >
          {resolvedTheme === 'dark' ? (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-muted-foreground">
              <circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
            </svg>
          ) : (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-muted-foreground">
              <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
            </svg>
          )}
        </button>

        {/* Connection status */}
        <ConnectionStatusIndicator
          connections={connections}
          aggregateStatus={aggregateStatus}
          menuOpen={connectionMenuOpen}
          onToggle={() => setConnectionMenuOpen(v => !v)}
          menuRef={connectionMenuRef}
        />

        {/* User context menu */}
        <UserContextMenu
          menuOpen={userMenuOpen}
          onToggle={() => setUserMenuOpen(v => !v)}
          menuRef={userMenuRef}
          onClose={() => setUserMenuOpen(false)}
        />

        {/* Mobile hamburger */}
        <button
          className="md:hidden flex items-center justify-center w-11 h-11 rounded text-muted-foreground hover:text-foreground hover:bg-surface transition-financial focus:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          onClick={toggleMobileMenu}
          aria-expanded={mobileMenuOpen}
          aria-label="Toggle navigation menu"
        >
          <Icon name={mobileMenuOpen ? 'XMarkIcon' : 'Bars3Icon'} size={20} variant="outline" />
        </button>
      </div>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div
          ref={mobileMenuRef}
          className="absolute top-header left-0 right-0 bg-card border-b border-border shadow-dropdown md:hidden z-dropdown"
        >
          <nav className="flex flex-col py-2" aria-label="Mobile navigation">
            {[...NAV_ITEMS, ...OVERFLOW_ITEMS].map((item) => (
              <Link
                key={item.path}
                href={item.path}
                onClick={() => { setMobileMenuOpen(false); onMenuToggle?.(false); }}
                className={`
                  flex items-center gap-3 px-6 py-3 text-sm font-medium transition-financial min-h-[44px]
                  ${isActive(item.path) ? 'text-primary bg-primary/10 border-l-2 border-primary' : 'text-card-foreground hover:bg-surface-elevated hover:text-foreground border-l-2 border-transparent'}
                `}
                aria-current={isActive(item.path) ? 'page' : undefined}
              >
                <Icon name={item.icon as any} size={18} variant="outline" />
                <span className="flex-1">{item.label}</span>
                <span className="text-xs text-muted-foreground font-data">{item.shortcut}</span>
              </Link>
            ))}
          </nav>
          {/* Mobile connection status */}
          <div className="px-6 py-3 border-t border-border flex items-center gap-2">
            <div className={`w-2 h-2 rounded-full ${statusColor[aggregateStatus]} pulse-live`} />
            <span className="text-xs text-muted-foreground font-caption">
              Data feeds: <span className={aggregateStatus === 'connected' ? 'text-success' : aggregateStatus === 'degraded' ? 'text-warning' : 'text-error'}>
                {statusLabel[aggregateStatus]}
              </span>
            </span>
          </div>
        </div>
      )}
    </header>
  );
};

interface ConnectionStatusProps {
  connections: ConnectionSource[];
  aggregateStatus: ConnectionSource['status'];
  menuOpen: boolean;
  onToggle: () => void;
  menuRef: React.RefObject<HTMLDivElement>;
}

const ConnectionStatusIndicator = ({ connections, aggregateStatus, menuOpen, onToggle, menuRef }: ConnectionStatusProps) => {
  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={onToggle}
        className="flex items-center gap-2 px-3 py-1.5 bg-surface rounded border border-border hover:border-primary/30 transition-financial focus:outline-none focus-visible:ring-1 focus-visible:ring-ring min-h-[36px]"
        aria-expanded={menuOpen}
        aria-haspopup="true"
        title="Data feed connection status"
      >
        <div className={`w-2 h-2 rounded-full ${statusColor[aggregateStatus]} ${aggregateStatus === 'connected' ? 'pulse-live' : ''}`} />
        <span className="hidden sm:block text-xs font-caption text-muted-foreground">
          {aggregateStatus === 'connected' ? 'Live' : aggregateStatus === 'degraded' ? 'Degraded' : 'Offline'}
        </span>
        <Icon name="ChevronDownIcon" size={12} variant="outline" className={`text-muted-foreground transition-financial ${menuOpen ? 'rotate-180' : ''}`} />
      </button>

      {menuOpen && (
        <div className="absolute top-full right-0 mt-1 w-64 bg-popover border border-border rounded-md shadow-dropdown z-dropdown overflow-hidden">
          <div className="px-4 py-3 border-b border-border">
            <p className="text-xs font-heading font-600 text-foreground uppercase tracking-wider">Data Feed Status</p>
          </div>
          {connections.map((conn) => (
            <div key={conn.name} className="flex items-center justify-between px-4 py-3 hover:bg-surface-elevated transition-financial">
              <div className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${statusColor[conn.status]} ${conn.status === 'connected' ? 'pulse-live' : ''}`} />
                <span className="text-sm text-card-foreground">{conn.name}</span>
              </div>
              <div className="flex items-center gap-2">
                {conn.latency !== undefined && (
                  <span className="font-data text-xs text-muted-foreground">{conn.latency}ms</span>
                )}
                <span className={`text-xs font-caption ${conn.status === 'connected' ? 'text-success' : conn.status === 'degraded' ? 'text-warning' : 'text-error'}`}>
                  {statusLabel[conn.status]}
                </span>
              </div>
            </div>
          ))}
          <div className="px-4 py-2 border-t border-border bg-surface">
            <p className="text-xs text-muted-foreground font-caption">
              Last updated: {new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

interface UserContextMenuProps {
  menuOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
  menuRef: React.RefObject<HTMLDivElement>;
}

const UserContextMenu = ({ menuOpen, onToggle, onClose, menuRef }: UserContextMenuProps) => {
  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={onToggle}
        className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-surface transition-financial focus:outline-none focus-visible:ring-1 focus-visible:ring-ring min-h-[36px] min-w-[44px] justify-center"
        aria-expanded={menuOpen}
        aria-haspopup="true"
        aria-label="User menu"
      >
        <div className="w-7 h-7 rounded bg-primary/20 border border-primary/30 flex items-center justify-center">
          <span className="font-heading font-600 text-xs text-primary">JD</span>
        </div>
        <div className="hidden sm:block text-left">
          <p className="text-xs font-medium text-foreground leading-none">J. Doe</p>
          <p className="text-xs text-muted-foreground font-caption mt-0.5">Inst. Trader</p>
        </div>
        <Icon name="ChevronDownIcon" size={12} variant="outline" className={`text-muted-foreground transition-financial hidden sm:block ${menuOpen ? 'rotate-180' : ''}`} />
      </button>

      {menuOpen && (
        <div className="absolute top-full right-0 mt-1 w-56 bg-popover border border-border rounded-md shadow-dropdown z-dropdown overflow-hidden">
          <div className="px-4 py-3 border-b border-border">
            <p className="text-sm font-medium text-foreground">John Doe</p>
            <p className="text-xs text-muted-foreground font-caption mt-0.5">Institutional Trader</p>
            <p className="text-xs text-muted-foreground font-data mt-0.5">j.doe@apexfi.com</p>
          </div>
          <div className="py-1">
            <Link
              href="/user-settings"
              onClick={onClose}
              className="flex items-center gap-3 px-4 py-2.5 text-sm text-card-foreground hover:bg-surface-elevated hover:text-foreground transition-financial min-h-[44px]"
            >
              <Icon name="Cog6ToothIcon" size={16} variant="outline" className="text-muted-foreground" />
              Settings
              <span className="ml-auto text-xs text-muted-foreground font-data">Alt+3</span>
            </Link>
            <Link
              href="/user-settings"
              onClick={onClose}
              className="flex items-center gap-3 px-4 py-2.5 text-sm text-card-foreground hover:bg-surface-elevated hover:text-foreground transition-financial min-h-[44px]"
            >
              <Icon name="KeyIcon" size={16} variant="outline" className="text-muted-foreground" />
              API Keys
            </Link>
            <Link
              href="/user-settings"
              onClick={onClose}
              className="flex items-center gap-3 px-4 py-2.5 text-sm text-card-foreground hover:bg-surface-elevated hover:text-foreground transition-financial min-h-[44px]"
            >
              <Icon name="BellIcon" size={16} variant="outline" className="text-muted-foreground" />
              Notifications
            </Link>
          </div>
          <div className="border-t border-border py-1">
            <button
              onClick={onClose}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-error hover:bg-error/10 transition-financial min-h-[44px]"
            >
              <Icon name="ArrowRightOnRectangleIcon" size={16} variant="outline" />
              Sign Out
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Header;